import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  NAIVE,
  BROKE,
  PIPELINE,
  UNDERSTAND,
  SEARCH,
  GUARD,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK : 3 vs 11 */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const eleven = interpolate(frame, [40, 72], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="rag pipeline · what the tutorials skip" color={COLORS.cyan} />
          <div style={{ display: "flex", alignItems: "baseline", gap: 36 }}>
            <span
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 150,
                color: COLORS.dim,
                letterSpacing: "-0.03em",
              }}
            >
              3 steps
            </span>
            <span style={{ fontFamily: FONTS.display, fontSize: 90, color: COLORS.dim }}>→</span>
            <span
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 200,
                color: COLORS.cyan,
                letterSpacing: "-0.03em",
                opacity: 0.4 + eleven * 0.6,
                textShadow: `0 0 60px ${COLORS.cyan}55`,
              }}
            >
              11
            </span>
          </div>
          <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 26 }}>
            every stage = one production failure
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — NAIVE : the 3-step tutorial RAG */
const NAIVE_PIPE: PipeNode[] = [
  { label: "1 · embed", value: "QUERY", sub: "→ vector", accent: "cyan" },
  { label: "2 · search", value: "VECTOR DB", sub: "top-k", accent: "green" },
  { label: "3 · generate", value: "LLM", sub: "answer", accent: "violet" },
];
export const Scene02Naive: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.naive.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="what every tutorial shows" color={COLORS.cyan} />
      <div style={{ width: 1280 }}>
        <PipelineGraph nodes={NAIVE_PIPE} startFrame={8} stepFrames={26} nodeWidth={300} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 46 }}>
        clean in a demo · falls apart in production
      </div>
    </AbsoluteFill>
    <Caption lines={NAIVE} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — BROKE : the failures */
const FAILS: { tag: string; text: string; accent: "red" | "amber" }[] = [
  { tag: "vague queries", text: "3-word questions → generic garbage", accent: "red" },
  { tag: "exact terms", text: "'pgvector' buried under similar content", accent: "red" },
  { tag: "no cache", text: "same query, full pipeline, 50×/day", accent: "amber" },
  { tag: "no access control", text: "tenant A could see tenant B's chunks", accent: "red" },
  { tag: "code = prose", text: "every query searched the same way", accent: "amber" },
];
export const Scene03Broke: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.broke.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="basic rag in production · what broke" color={COLORS.red} />
        <div style={{ display: "flex", flexDirection: "column", gap: 16, width: 1180 }}>
          {FAILS.map((f, i) => {
            const appear = interpolate(frame, [10 + i * 18, 28 + i * 18], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={f.tag}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 22,
                  opacity: appear,
                  transform: `translateX(${(1 - appear) * -24}px)`,
                  background: COLORS.panel,
                  border: `1px solid ${COLORS.line}`,
                  borderLeft: `4px solid ${COLORS[f.accent]}`,
                  borderRadius: 10,
                  padding: "16px 24px",
                }}
              >
                <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 30, color: COLORS[f.accent] }}>✕</span>
                <span
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 19,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: COLORS[f.accent],
                    width: 300,
                  }}
                >
                  {f.tag}
                </span>
                <span style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.text }}>{f.text}</span>
              </div>
            );
          })}
        </div>
      </AbsoluteFill>
      <Caption lines={BROKE} accent="red" />
    </AbsoluteFill>
  );
};

/* 04 — PIPELINE : the 11-stage map */
const STAGES: { n: number; label: string; phase: 0 | 1 | 2 }[] = [
  { n: 1, label: "classify", phase: 0 },
  { n: 2, label: "route", phase: 0 },
  { n: 3, label: "expand", phase: 0 },
  { n: 4, label: "rewrite", phase: 0 },
  { n: 5, label: "cache", phase: 1 },
  { n: 6, label: "embed", phase: 2 },
  { n: 7, label: "hybrid search", phase: 2 },
  { n: 8, label: "access filter", phase: 2 },
  { n: 9, label: "rerank", phase: 2 },
  { n: 10, label: "graph augment", phase: 2 },
  { n: 11, label: "generate", phase: 2 },
];
const PHASE_COLOR = [COLORS.cyan, COLORS.amber, COLORS.green] as const;
export const Scene04Pipeline: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.pipeline.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the eleven-stage pipeline" color={COLORS.green} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 14,
            width: 1380,
          }}
        >
          {STAGES.map((s, i) => {
            const c = PHASE_COLOR[s.phase];
            const appear = interpolate(frame, [8 + i * 9, 22 + i * 9], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={s.n}
                style={{
                  opacity: appear,
                  transform: `translateY(${(1 - appear) * 14}px)`,
                  background: COLORS.panel,
                  border: `1px solid ${c}66`,
                  borderRadius: 10,
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 32, color: c }}>{s.n}</span>
                <span style={{ fontFamily: FONTS.mono, fontSize: 21, color: COLORS.text }}>{s.label}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 36, marginTop: 30, fontFamily: FONTS.mono, fontSize: 18 }}>
          <span style={{ color: COLORS.cyan }}>● understand</span>
          <span style={{ color: COLORS.amber }}>● short-circuit cache</span>
          <span style={{ color: COLORS.green }}>● retrieve + generate</span>
        </div>
      </AbsoluteFill>
      <Caption lines={PIPELINE} accent="green" />
    </AbsoluteFill>
  );
};

/* 05 — UNDERSTAND : stages 1–4 */
const UNDERSTAND_PIPE: PipeNode[] = [
  { label: "1 · classify", value: "INTENT", sub: "factual…code", accent: "cyan" },
  { label: "2 · route", value: "STRATEGY", sub: "passes · top-k", accent: "green" },
  { label: "3 · expand", value: "ALIASES", sub: "admin-only", accent: "lime" },
  { label: "4 · rewrite", value: "RICHER Q", sub: "LLM synonyms", accent: "violet" },
];
export const Scene05Understand: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.understand.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="phase 1 · understand the question" color={COLORS.cyan} />
      <div style={{ width: 1640 }}>
        <PipelineGraph nodes={UNDERSTAND_PIPE} startFrame={6} stepFrames={20} nodeWidth={220} />
      </div>
      <div style={{ display: "flex", gap: 24, marginTop: 48 }}>
        <Callout label="heuristic-first" accent="cyan" width={400}>
          <b style={{ color: COLORS.text }}>80%+</b> of queries classified in <b style={{ color: COLORS.text }}>&lt;1ms</b>, $0 API
        </Callout>
        <Callout label="query-classifier.ts" accent="green" width={400}>
          pattern scores → LLM fallback only when ambiguous
        </Callout>
      </div>
    </AbsoluteFill>
    <Caption lines={UNDERSTAND} accent="cyan" />
  </AbsoluteFill>
);

/* 06 — SEARCH : hybrid + the silent BM25 bug */
const SQL = `-- 009_hybrid_search_function.sql   (0.7 vector + 0.3 text)
with vector_results as (
  select id, 1 - (embedding_vector <=> query_embedding) as vec_sim
  from document_chunks
  where tenant_id = match_tenant_id and is_deleted = false
  order by embedding_vector <=> query_embedding
  limit match_count * 3            -- over-fetch 3x for the reranker
),
text_results as (
  select id, ts_rank_cd(search_tsvector,
    plainto_tsquery('english', query_text)) as text_rank   -- AND-semantics!
  from document_chunks
  where search_tsvector @@ plainto_tsquery('english', query_text)
)
select id,
  (vec_sim * 0.7) + (coalesce(text_rank, 0) * 0.3) as combined_score
from vector_results left join text_results using (id)
order by combined_score desc limit match_count;`;
export const Scene06Search: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.search.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 36 }}>
      <CodePanel
        code={SQL}
        language="sql"
        filename="009_hybrid_search_function.sql"
        highlightLines={[10, 11]}
        revealFrames={300}
        fontSize={22}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={SEARCH} accent="green" />
  </AbsoluteFill>
);

/* 07 — GUARD : access control + rerank */
export const Scene07Guard: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.guard.duration} />
    <AbsoluteFill style={center}>
      <Kicker text="phase 3 · filter, then polish" color={COLORS.amber} />
      <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
        <Panel accent="amber" title="stage 8 · access control" width={520}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.9, color: COLORS.text }}>
            <div>mode <span style={{ color: COLORS.green }}>'all'</span> → everyone in tenant</div>
            <div>mode <span style={{ color: COLORS.cyan }}>'restricted'</span> → allow-list only</div>
            <div style={{ color: COLORS.amber, marginTop: 8 }}>unknown mode → <b>DENY</b></div>
            <div style={{ color: COLORS.dim, fontSize: 17 }}>fail-closed · access-control.ts</div>
          </div>
        </Panel>
        <Panel accent="green" title="stage 9 · llm reranking" width={520}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.9, color: COLORS.text }}>
            <div>BATCH_SIZE = <span style={{ color: COLORS.green }}>5</span> chunks / call</div>
            <div>score each 0–10 for relevance</div>
            <div style={{ color: COLORS.green, marginTop: 8 }}>kills embedding false-positives</div>
            <div style={{ color: COLORS.dim, fontSize: 17 }}>reranker.ts</div>
          </div>
        </Panel>
      </div>
    </AbsoluteFill>
    <Caption lines={GUARD} accent="amber" />
  </AbsoluteFill>
);

/* 08 — PAYOFF : 75% → peak 90% */
export const Scene08Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [200, 230], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="code-query retrieval accuracy" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.dim }}>~75%</span>
          <span style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</span>
          <MetricCounter value={90} from={75} startFrame={10} durationFrames={60} accent="lime" suffix="%" fontSize={150} />
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 19, color: COLORS.dim, marginTop: 6 }}>
          peak · doc queries already ~90%, unchanged
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 38 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="mig 033 · OR-tsquery" accent="green" width={380}>
              BM25 results: <b style={{ color: COLORS.text }}>0 → 15–20</b>
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="mig 034 · camelCase split" accent="lime" width={380}>
              broke the <b style={{ color: COLORS.text }}>85% ceiling</b> → peak 90%
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          11 stages isn't over-engineering.
          <br />
          <span style={{ color: COLORS.cyan }}>It's 11 failures, each made a stage.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · grounded retrieval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
